import { useElements, useStripe } from "@stripe/react-stripe-js";
import ButtonJustifyEnd from "components/ButtonJustifyEnd";
import Loading from "components/Loading";
import PhoneInputAuth from "components/PhoneInputAuth";
import SelectInputAuth from "components/SelectInputAuth";
import {
  EElementType,
  StripeInputElement,
} from "components/StripeInputElement";
import TextInputAuth from "components/TextInputAuth";
import Title from "components/Title";
import handleErrors from "helpers/handleErrors";
import { statesUS } from "helpers/handleStates";
import toastMessage from "helpers/handleToastMessage";
import IconArrowRight from "icons/arrow-circle-right-white.svg";
import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { CreateGroupSetBillingInformationStep1 } from "services/manageFamilies";
import { schemaPaymentsDetails } from "./validation";

type Props = {
  steps: number;
  setSteps: React.Dispatch<React.SetStateAction<number>>;
};

const PaymentDetails: React.FC<Props> = ({ steps, setSteps }): JSX.Element => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<{ id: string; name: string }[]>();

  useEffect(() => {
    const processedStates = statesUS.map((state: any) => {
      return { id: state.abbreviation, name: state.name };
    });

    setStates(processedStates);
  }, []);

  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [state, setState] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [errors, setErrors] = useState<any>();

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }
    try {
      setLoading(true);

      const cardElement = elements.getElement("cardNumber");

      if (!cardElement) {
        toastMessage({
          message: "Error, Something went wrong.",
          type: "error",
        });
        return;
      }

      const result = await stripe.createToken(cardElement, {
        name: "",
      });

      if (!result.token) {
        toastMessage({
          message: "Error, Something went wrong.",
          type: "error",
        });
        return;
      }

      const stateData = {
        fullName,
        address,
        city,
        zip,
        state,
        phone,
        email,
      };

      if (!schemaPaymentsDetails.isValidSync(stateData)) {
        handleErrors({
          schema: schemaPaymentsDetails,
          stateData: stateData,
          setErrorMessage: setErrors,
        });
        setLoading(false);
        return;
      }

      const { data } = await CreateGroupSetBillingInformationStep1({
        address,
        city,
        state,
        zip,
        country: "US",
        email,
        phoneNumber: phone,
        cardName: fullName,
        creditCardTokenId: result.token.id,
        cardBrand: result.token.card?.brand,
        cardExpiryMonth: result.token.card?.exp_month,
        cardExpiryYear: result.token.card?.exp_year,
        cardLast4: result.token.card?.last4,
      });

      if (data) {
        toastMessage({
          message: "Payment created successfully.",
          type: "success",
        });
        localStorage.setItem("groupId", data.groupId);
        if (steps === 1) {
          setSteps(2);
        }
      }
    } catch (error: any) {
      toastMessage({
        message: "Error, Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      setFullName("John Doe");
      setAddress("33 Street Brazil");
      setAddress("33 Street Brazil");
      setCity("Imaginópolis");
      setZip("44444");
      setPhone("3213213213");
      setEmail("johndoe@gmail.com");
    }
  }, []);

  return (
    <div className="bg-bt-white p-4">
      <Loading loading={loading} />
      <div className="w-9/12 mx-auto mt-4">
        <Title title={"Payment Details"} />
        <div className="flex flex-row">
          <div className="flex flex-col w-1/2 mx-2">
            <TextInputAuth
              placeholder={"Enter the name visible on your credit card"}
              label="Cardholder Name"
              value={fullName}
              onChange={(e: any) => setFullName(e.target.value)}
              isHelp={errors?.fullName}
              helpText={errors?.fullName}
            />
            <StripeInputElement type={EElementType.Number} />
            <StripeInputElement type={EElementType.CVC} />
            <StripeInputElement type={EElementType.Expiry} />
          </div>
          <div className="flex flex-col w-1/2 mx-2">
            <TextInputAuth
              placeholder={"Enter your address"}
              label="Address"
              value={address}
              onChange={(e: any) => setAddress(e.target.value)}
              isHelp={errors?.address}
              helpText={errors?.address}
            />
            <TextInputAuth
              placeholder={"Enter your city"}
              label="City"
              value={city}
              onChange={(e: any) => setCity(e.target.value)}
              isHelp={errors?.city}
              helpText={errors?.city}
            />
            <div className="flex flex-row w-full">
              <div className="flex flex-col w-1/2 mr-2">
                <TextInputAuth
                  placeholder={"Enter your ZIP Code"}
                  label="ZIP Code"
                  value={zip}
                  onChange={(e: any) => setZip(e.target.value)}
                  isHelp={errors?.zip}
                  helpText={errors?.zip}
                  maxLength={5}
                />
              </div>
              <div className="flex flex-col w-1/2 ml-1">
                <SelectInputAuth
                  placeholder={"Enter your state"}
                  label="State"
                  value={state}
                  onChange={(e: any) => setState(e.target.value)}
                  isHelp={errors?.state}
                  helpText={errors?.state}
                  options={states || []}
                />
              </div>
            </div>
            <PhoneInputAuth
              placeholder={"(123) 456-7890"}
              label="Phone"
              value={phone}
              onChange={(e: any) => setPhone(e.target.value)}
              isHelp={errors?.phone}
              helpText={errors?.phone}
            />
            <TextInputAuth
              placeholder={"Enter your email"}
              label="Email"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              isHelp={errors?.email}
              helpText={errors?.email}
            />
          </div>
        </div>
      </div>

      <ButtonJustifyEnd
        title={"Continue"}
        iconRight={IconArrowRight}
        action={handleSubmit}
      />

      <ToastContainer />
    </div>
  );
};

export default PaymentDetails;
